import * as ngCdkDragDrop from '@angular/cdk/drag-drop'
import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as directives from '../../../../directives'
import * as input from '../../../input'
import { AtnDsTableStore } from '../../store'

/**
 * Minimum allowed width of a single column, in pixels
 */
const MIN_COLUMN_WIDTH_IN_PIXELS = 30
/**
 * By default, we allow columns to render however they will, and then we measure
 * their current width and use that as the initial width. However, this
 * sometimes results in tiny columns. Those tiny columns may be acceptable if
 * the user had resized the column manually, but as a default state, they are
 * not very user-friendly. For that reason, we have a larger minimum for the
 * initial widths than we do for the resized widths.
 */
const MIN_INITIAL_COLUMN_WIDTH_IN_PIXELS = 100
/**
 * We don't usually want a column to start too large, as that results in a lot
 * of horizontal scrolling
 */
const MAX_INITIAL_COLUMN_WIDTH_IN_PIXELS =
  2 * MIN_INITIAL_COLUMN_WIDTH_IN_PIXELS

@ngCore.Component({
  imports: [
    ngCommon.NgStyle,
    ngCdkDragDrop.DragDropModule,
    input.checkbox.Component,
    directives.measuredElement.Directive,
  ],
  selector: 'table-column-headers',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<TTypeParams extends ec.models.TypeParams.Constraint> {
  /**
   * Reference to all {@link directives.measuredElement.Directive Measured Elements} of this component.
   */
  private readonly _measuredElementsSg = ngCore.viewChildren(
    directives.measuredElement.Directive<ents.core.common.Attribute>,
  )
  /** @see {@link AtnDsTableStore} */
  public readonly store = ngCore.inject(AtnDsTableStore)
  /**
   * Column {@link ents.core.common.Attribute.Id id}  that is currently being sorted.
   */
  public readonly sortByIdSg = ngCore.computed(
    (plugin = this.store.pluginSg()) =>
      plugin?.collection.plugins.currentListPlugin.appliedParams.selectors.sortBy.asSignal(),
  )
  /**
   * Direction of the sorting of the {@link sortByIdSg column} that is currently being sorted.
   */
  public readonly isDescSortSg = ngCore.computed(
    (plugin = this.store.pluginSg()) =>
      !!plugin?.collection.plugins.currentListPlugin.appliedParams.selectors.descending.asSignal(),
  )

  constructor() {
    /**
     * Each time a new {@link directives.measuredElement.Directive Measured Element} is added to the view,
     * it's initial width is set to the {@link AtnDsTableStore.columnWidthsSg columnWidths} state.
     */
    ngCore.effect(() => {
      /** @see {@link _measuredElementsSg} */
      const measuredElements = this._measuredElementsSg()

      for (const measuredElement of measuredElements) {
        const id = measuredElement.context().id

        // Measures the actual initial width of the column
        let initialWidth = measuredElement.initialWidthSg()
        if (initialWidth === 0) {
          // We do not want to set the minimum width if it is still 0
          continue
        }

        // If it is less than the minimum allowed width, we must use the minimum
        // width
        initialWidth = Math.min(
          Math.max(initialWidth, MIN_INITIAL_COLUMN_WIDTH_IN_PIXELS),
          MAX_INITIAL_COLUMN_WIDTH_IN_PIXELS,
        )

        // Then we write the initial width, if there is no current width
        this.store.updateColumnWidth(id, width => width ?? initialWidth)
        this.store.updateMaxColumnWidth(id, initialWidth)
      }
    })
  }

  /**
   * Dispatches sorting action upon clicking the column name
   */
  public columnNameClickHandler(attribute: ec.models.Attribute<TTypeParams>) {
    const plugin = this.store.pluginSg()
    const isDescending = this.isDescSortSg()

    const source = `Table.ColumnHeaders.${attribute.id}.SortArrow.Clicked`
    plugin?.collection.plugins.currentListPlugin.appliedParams.actions.setSortBy
      .from(source)
      .dispatch(attribute.id)
    plugin?.collection.plugins.currentListPlugin.appliedParams.actions.setDescending
      .from(source)
      .dispatch(!isDescending)
  }

  /**
   * Calculating new column width upon resizing.
   *
   * We have minimal column width so if we have drag the column smaller than min
   * width, we set the column width to min column width.
   */
  public columnResizeDroppedHandler(
    event: ngCdkDragDrop.CdkDragDrop<any>,
    attribute: ec.models.Attribute<TTypeParams>,
  ) {
    const currentWidth =
      this.store.columnWidthsSg()[attribute.id] ?? MIN_COLUMN_WIDTH_IN_PIXELS

    const {
      distance: { x: draggedWidth },
    } = event

    this.store.updateColumnWidth(
      attribute.id,
      _calculateNewColumnWidth(currentWidth, draggedWidth),
    )
  }

  /**
   * Resizes the column to display the whole content
   */
  public columnDblClickHandler(attribute: ec.models.Attribute<TTypeParams>) {
    const columnMaxWidth = this.store.getMaxColumnWidthSg(attribute.id)()
    if (columnMaxWidth) {
      // NOTE: Adding 20 to account for cell paddings and 10 for aestethic reasons, since
      // columnMaxWidth contains only width of the textual content.
      this.store.updateColumnWidth(attribute.id, columnMaxWidth + 30)
    }
  }

  /** TODO: */
  public onAllSelectChangeHandler() {
    this.store.allRowSelectionToggle()
  }
}

/** TODO: */
function _calculateNewColumnWidth(
  currentWidth: number,
  draggedWidth: number,
): number {
  let newWidth = currentWidth + draggedWidth

  if (newWidth < MIN_COLUMN_WIDTH_IN_PIXELS) {
    newWidth = MIN_COLUMN_WIDTH_IN_PIXELS
  }

  return newWidth
}
